import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Calculator, CheckCircle, Clock, DollarSign, Heart, Users, Shield, Zap, Sparkles, Award, Star } from "lucide-react";
import Layout from "@/components/Layout";
import { useCountUp } from "@/hooks/useCountUp";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { TestimonialCarousel } from "@/components/TestimonialCarousel";
import { TaxTipCard } from "@/components/TaxTipCard";
import { HeroImage } from "@/components/HeroImage";

const Home = () => {
  const benefits = [
    { icon: Clock, title: "38 Years Experience", description: "Trusted expertise serving Kansas families" },
    { icon: Heart, title: "Friendly Service", description: "Personal, one-on-one attention" },
    { icon: DollarSign, title: "Affordable Rates", description: "Priced to accommodate all types of budgets" },
    { icon: CheckCircle, title: "Fast & Secure", description: "Quick turnaround, secure e-filing" },
  ];

  const stats = [
    { icon: Clock, value: 38, suffix: "+", label: "Years Experience" },
    { icon: Users, value: 1000, suffix: "+", label: "Happy Clients" },
    { icon: Shield, value: 100, suffix: "%", label: "Secure" },
    { icon: Zap, value: 3, suffix: " Days", label: "Avg. Turnaround" },
  ];

  const { ref: statsRef, isVisible: statsVisible } = useScrollAnimation(0.2);
  const { ref: benefitsRef, isVisible: benefitsVisible } = useScrollAnimation(0.1);

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary via-secondary to-accent text-primary-foreground overflow-hidden">
        <div className="absolute inset-0">
          <HeroImage
            src="/images/hero-brand-image.png"
            alt="The Tiny Tax Lady - friendly professional tax preparer at her desk"
            priority={true}
            width={1920}
            height={1080}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-primary/40 via-secondary/40 to-accent/40" />
        </div>
        
        <div className="container mx-auto px-4 py-20 md:py-32 relative z-10">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight drop-shadow-lg">
              Personal Tax Help from Your Neighborhood Expert
            </h1>
            <p className="text-xl md:text-2xl text-primary-foreground/90">
              38 years of experience serving individuals and sole proprietors in Lenexa and the Kansas City area
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <Button asChild size="lg" variant="secondary" className="text-lg">
                <Link to="/schedule-appointment">Book Your Appointment</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-lg bg-white/10 hover:bg-white/20 border-white/30">
                <Link to="/services">Learn More</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section ref={statsRef} className="py-12 bg-gradient-to-r from-muted/30 via-background to-muted/30 border-y border-border">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => {
              const count = useCountUp(stat.value, 2000, statsVisible);
              return (
                <div key={index} className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <stat.icon className="h-6 w-6 text-secondary" />
                    <div className="text-3xl md:text-4xl font-bold text-secondary">
                      {statsVisible ? count : 0}{stat.suffix}
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground font-medium">{stat.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section ref={benefitsRef} className="py-16 md:py-24 bg-gradient-to-br from-primary/10 via-secondary/10 to-accent/10 relative overflow-hidden">
        {/* Tiny Tax Lady - Full Opacity */}
        <div className="absolute top-10 left-10 w-32 h-32 rounded-full bg-gradient-to-br from-gray-200 to-gray-300 animate-float-shapes flex items-center justify-center shadow-xl pointer-events-none z-10">
          <img 
            src="/images/logo-tiny-tax-lady.png" 
            alt="Tiny Tax Lady" 
            className="w-20 h-20 rounded-full object-cover shadow-lg"
          />
        </div>

        {/* Floating Shapes Background */}
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-40 right-20 w-20 h-20 bg-secondary rotate-45 animate-float-shapes" style={{ animationDelay: '1s' }} />
          <div className="absolute bottom-20 left-1/4 w-24 h-24 rounded-full bg-accent animate-float-shapes" style={{ animationDelay: '2s' }} />
          <div className="absolute bottom-40 right-1/3 w-16 h-16 bg-primary rotate-12 animate-float-shapes" style={{ animationDelay: '1.5s' }} />
        </div>
        
        {/* Dot Grid Pattern */}
        <div className="absolute inset-0 dot-grid opacity-5 text-primary pointer-events-none" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in">
            <div className="inline-block mb-4 animate-pulse-glow">
              <span className="px-6 py-3 bg-gradient-to-r from-primary to-secondary text-white rounded-full text-sm font-bold shadow-xl border-2 border-white/30">
                ✨ Why Choose Us
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
              Tax Time Doesn't Have to Be Stressful
            </h2>
            <p className="text-lg text-muted-foreground">
              One-on-one service from a seasoned pro who keeps things simple, accurate, and affordable. We use a full technology suite for your convenience and security.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
      {
        icon: Award,
        title: "38 Years Experience",
        desc: "Trusted expertise serving Kansas families",
        number: "01",
        bgGradient: "from-blue-500/10 to-sky-500/5",
        iconBg: "from-blue-500/40 to-sky-500/30",
        iconColor: "text-blue-600 dark:text-blue-400",
        borderColor: "border-blue-500/30 hover:border-blue-500",
        shadowColor: "hover:shadow-blue-500/20",
        badgeGradient: "from-blue-500 to-sky-600",
        hoverGlow: "group-hover:shadow-[0_0_30px_rgba(59,130,246,0.3)]"
      },
              { 
                icon: Heart, 
                title: "Friendly Service", 
                desc: "Personal, one-on-one attention", 
                number: "02",
                bgGradient: "from-secondary/10 to-secondary/5",
                iconBg: "from-secondary/40 to-secondary/30",
                iconColor: "text-secondary",
                borderColor: "border-secondary/30 hover:border-secondary",
                shadowColor: "hover:shadow-secondary/20",
                badgeGradient: "from-secondary to-secondary/80",
                hoverGlow: "group-hover:shadow-[0_0_30px_rgba(var(--secondary),0.3)]"
              },
              { 
                icon: DollarSign, 
                title: "Affordable Rates", 
                desc: "Priced to accommodate all types of budgets",
                number: "03",
                bgGradient: "from-accent/10 to-accent/5",
                iconBg: "from-accent/40 to-accent/30",
                iconColor: "text-accent",
                borderColor: "border-accent/30 hover:border-accent",
                shadowColor: "hover:shadow-accent/20",
                badgeGradient: "from-accent to-accent/80",
                hoverGlow: "group-hover:shadow-[0_0_30px_rgba(var(--accent),0.3)]"
              },
              { 
                icon: Shield, 
                title: "Fast & Secure", 
                desc: "Quick turnaround, secure e-filing", 
                number: "04",
                bgGradient: "from-purple-500/10 to-violet-500/5",
                iconBg: "from-purple-500/40 to-violet-500/30",
                iconColor: "text-purple-600 dark:text-purple-400",
                borderColor: "border-purple-500/30 hover:border-purple-500",
                shadowColor: "hover:shadow-purple-500/20",
                badgeGradient: "from-purple-500 to-violet-600",
                hoverGlow: "group-hover:shadow-[0_0_30px_rgba(168,85,247,0.3)]"
              }
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <Card 
                  key={index} 
                  className={`p-6 border-2 ${item.borderColor} ${item.shadowColor} ${item.hoverGlow} hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 relative overflow-hidden group bg-gradient-to-br ${item.bgGradient}`}
                  style={{ 
                    animationDelay: benefitsVisible ? `${index * 100}ms` : '0ms',
                    opacity: benefitsVisible ? 1 : 0,
                    transform: benefitsVisible ? 'translateY(0)' : 'translateY(20px)',
                    transition: 'opacity 0.6s ease-out, transform 0.6s ease-out'
                  }}
                >
                  {/* Number Badge */}
                  <div className={`absolute top-4 right-4 w-10 h-10 rounded-full bg-gradient-to-br ${item.badgeGradient} flex items-center justify-center text-white font-bold text-sm shadow-lg group-hover:scale-110 group-hover:animate-pulse-glow transition-transform duration-300`}>
                    {item.number}
                  </div>
                  
                  {/* Gradient Border Animation */}
                  <div className={`absolute inset-0 bg-gradient-to-r ${item.bgGradient} opacity-0 group-hover:opacity-30 transition-opacity duration-300 rounded-lg`} />
                  
                  <div className="flex flex-col items-center text-center space-y-4 relative z-10">
                    <div className={`p-4 rounded-full bg-gradient-to-br ${item.iconBg} group-hover:scale-110 transition-transform duration-300 shadow-md`}>
                      <Icon className={`w-8 h-8 ${item.iconColor}`} />
                    </div>
                    <h3 className="text-xl font-bold">{item.title}</h3>
                    <p className="text-muted-foreground">{item.desc}</p>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-24 bg-background relative overflow-hidden">
        {/* Diagonal Stripes Background */}
        <div className="absolute inset-0 diagonal-stripes opacity-5 text-primary pointer-events-none" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12 relative">
              {/* Floating decorative elements */}
              <div className="absolute -top-4 left-1/4 w-12 h-12 bg-primary/10 rounded-full blur-xl animate-pulse" />
              <div className="absolute -top-2 right-1/4 w-8 h-8 bg-secondary/10 rounded-full blur-lg animate-pulse" style={{ animationDelay: '2s' }} />
              
              {/* Main title with effects */}
              <h2 className="text-4xl md:text-5xl font-bold relative inline-block">
                <span className="absolute inset-0 bg-gradient-to-r from-primary via-secondary to-accent opacity-20 blur-2xl animate-pulse" />
                <span className="relative bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
                  Why Families Choose Us
                </span>
              </h2>
              
              {/* Decorative underline */}
              <div className="flex items-center justify-center gap-2 mt-4">
                <div className="h-1 w-20 bg-gradient-to-r from-transparent via-primary to-secondary rounded-full animate-shimmer" />
                <Heart className="w-5 h-5 text-secondary fill-secondary animate-pulse" />
                <div className="h-1 w-20 bg-gradient-to-r from-secondary via-primary to-transparent rounded-full animate-shimmer" />
              </div>
            </div>
            
            <div className="space-y-8 relative">
              {/* Timeline Connector */}
              <div className="absolute left-6 top-0 bottom-0 w-1 bg-gradient-to-b from-primary via-secondary to-accent opacity-30 hidden md:block" />
              
              {/* Commitment Card 1 */}
              <div className="relative animate-fade-in">
                <div className="absolute -left-2 top-8 w-5 h-5 rounded-full bg-primary border-4 border-background hidden md:block" />
                <Card className="p-8 border-l-4 border-primary hover:shadow-xl transition-all duration-300 hover:-translate-y-1 ml-0 md:ml-12 group relative overflow-hidden">
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <svg className="w-8 h-8 text-primary animate-draw-line" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold mb-3 flex items-center gap-3">
                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary text-white text-sm font-bold shadow-lg">
                      01
                    </span>
                    38 Years of Experience
                  </h3>
                  <p className="text-muted-foreground text-lg">
                    Decades of trusted tax preparation expertise serving Kansas families
                  </p>
                </Card>
              </div>
              
              {/* Commitment Card 2 */}
              <div className="relative animate-fade-in" style={{ animationDelay: '0.1s' }}>
                <div className="absolute -left-2 top-8 w-5 h-5 rounded-full bg-secondary border-4 border-background hidden md:block" />
                <Card className="p-8 border-l-4 border-secondary hover:shadow-xl transition-all duration-300 hover:-translate-y-1 ml-0 md:ml-12 group relative overflow-hidden">
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <svg className="w-8 h-8 text-secondary animate-draw-line" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold mb-3 flex items-center gap-3">
                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-secondary to-accent text-white text-sm font-bold shadow-lg">
                      02
                    </span>
                    Friendly, Local Service
                  </h3>
                  <p className="text-muted-foreground text-lg">
                    Personalized attention from your neighborhood tax expert
                  </p>
                </Card>
              </div>
              
              {/* Commitment Card 3 */}
              <div className="relative animate-fade-in" style={{ animationDelay: '0.2s' }}>
                <div className="absolute -left-2 top-8 w-5 h-5 rounded-full bg-accent border-4 border-background hidden md:block" />
                <Card className="p-8 border-l-4 border-accent hover:shadow-xl transition-all duration-300 hover:-translate-y-1 ml-0 md:ml-12 group relative overflow-hidden">
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <svg className="w-8 h-8 text-accent animate-draw-line" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold mb-3 flex items-center gap-3">
                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-accent to-primary text-white text-sm font-bold shadow-lg">
                      03
                    </span>
                    Time Saving Technology
                  </h3>
                  <p className="text-muted-foreground text-lg">
                    Upload your documents for prompt review 24/7.
                  </p>
                </Card>
              </div>
              
              {/* Featured Lightning-Fast Card */}
              <div className="relative animate-fade-in" style={{ animationDelay: '0.3s' }}>
                <div className="absolute -left-2 top-8 w-5 h-5 rounded-full bg-primary border-4 border-background hidden md:block" />
                <Card className="p-8 border-l-4 border-primary hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 ml-0 md:ml-12 relative overflow-hidden bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5">
                  {/* Featured Badge */}
                  <div className="absolute top-4 right-4">
                    <span className="inline-block bg-gradient-to-r from-primary to-secondary text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg animate-pulse-glow">
                      ⚡ FEATURED
                    </span>
                  </div>
                  
                  {/* Glowing Border Effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-primary via-secondary to-accent opacity-20 rounded-lg animate-shimmer" />
                  
                  <div className="relative z-10">
                    <h3 className="text-2xl font-bold mb-3 flex items-center gap-3">
                      <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-primary via-secondary to-accent text-white text-sm font-bold shadow-lg animate-pulse">
                        ⚡
                      </span>
                      Lightning-Fast & Secure Filing
                    </h3>
                    
                    <div className="space-y-4 mt-6">
                      <div className="p-4 rounded-lg bg-background/80 backdrop-blur-sm border border-primary/20 hover:border-primary/40 transition-colors">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="flex-shrink-0 w-16 h-16 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center shadow-lg">
                            <span className="text-2xl font-bold text-white">2-7</span>
                          </div>
                          <div>
                            <div className="font-bold text-lg">Days</div>
                            <div className="text-sm text-muted-foreground">Most returns completed in record time</div>
                          </div>
                        </div>
                        <Link to="/new-client" className="block">
                          <Button className="w-full">Start Your Taxes 24/7 Now without Appointment Needed</Button>
                        </Link>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-center gap-3 p-4 rounded-lg bg-background/80 backdrop-blur-sm border border-secondary/20 hover:border-secondary/40 transition-colors group">
                          <Shield className="w-6 h-6 text-secondary group-hover:scale-110 transition-transform" />
                          <span className="text-sm font-medium">Bank-level encryption keeps your data safe</span>
                        </div>
                        
                        <div className="flex items-center gap-3 p-4 rounded-lg bg-background/80 backdrop-blur-sm border border-accent/20 hover:border-accent/40 transition-colors group">
                          <CheckCircle className="w-6 h-6 text-accent group-hover:scale-110 transition-transform" />
                          <span className="text-sm font-medium">IRS-approved secure e-filing technology</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tax Tips Section */}
      <section className="py-16 bg-gradient-to-r from-accent/20 via-secondary/20 to-primary/20 relative overflow-hidden">
        {/* Light Bulb SVG Background */}
        <div className="absolute top-10 right-10 opacity-10 animate-float-shapes pointer-events-none hidden lg:block">
          <svg width="120" height="120" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" className="text-primary">
            <path d="M9 18h6" />
            <path d="M10 22h4" />
            <path d="M15 2a7 7 0 0 1-1 13.95V18H10v-2.05A7 7 0 0 1 15 2z" />
          </svg>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto">
            {/* Pushpin Graphics & Sticky Note Card */}
            <div className="relative">
              <div className="absolute -top-6 left-8 w-6 h-6 bg-destructive rounded-full shadow-lg animate-tilt z-20" />
              <div className="absolute -top-6 right-8 w-6 h-6 bg-primary rounded-full shadow-lg animate-tilt z-20" style={{ animationDelay: '1s' }} />
              
              {/* Pro Tip Ribbon Badge */}
              <div className="absolute -left-4 top-8 bg-gradient-to-r from-primary to-secondary text-white px-4 py-2 rounded-r-lg shadow-xl text-sm font-bold z-20 animate-pulse-glow">
                💡 PRO TIP
              </div>
              
              {/* Sticky Note Card */}
              <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-900/20 dark:to-yellow-800/20 p-8 rounded-lg shadow-2xl border-2 border-yellow-200 dark:border-yellow-700 relative transform hover:rotate-1 transition-transform duration-300">
                {/* Paper Texture Overlay */}
                <div className="absolute inset-0 opacity-5 pointer-events-none bg-[repeating-linear-gradient(0deg,transparent,transparent_2px,currentColor_2px,currentColor_3px)] rounded-lg" />
                
                <div className="relative z-10">
                  <TaxTipCard />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5 relative overflow-hidden">
        {/* Spotlight Effect */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,hsl(var(--background))_100%)] pointer-events-none" />
        
        {/* Floating Quote Marks */}
        <div className="absolute top-20 left-10 text-primary/10 text-9xl font-serif pointer-events-none animate-float-shapes">"</div>
        <div className="absolute bottom-20 right-10 text-secondary/10 text-9xl font-serif pointer-events-none rotate-180 animate-float-shapes" style={{ animationDelay: '2s' }}>"</div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12 relative">
              {/* Floating quote marks as decoration */}
              <div className="absolute -top-8 left-10 text-8xl text-primary/5 font-serif">"</div>
              <div className="absolute -bottom-8 right-10 text-8xl text-secondary/5 font-serif rotate-180">"</div>
              
              {/* Animated badge */}
              <div className="inline-block mb-4">
                <div className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary via-secondary to-accent text-white rounded-full shadow-lg relative overflow-hidden">
                  <span className="absolute inset-0 bg-white/20 animate-pulse" />
                  <Star className="w-5 h-5 fill-white animate-pulse" />
                  <span className="relative z-10 text-sm font-bold tracking-wide">5-STAR VERIFIED REVIEWS</span>
                  <Star className="w-5 h-5 fill-white animate-pulse" style={{ animationDelay: '0.5s' }} />
                </div>
              </div>
              
              {/* Enhanced title */}
              <h2 className="text-4xl md:text-5xl font-bold mb-4 relative inline-block">
                <span className="absolute inset-0 bg-gradient-to-r from-primary via-secondary to-accent opacity-20 blur-2xl animate-pulse" />
                <span className="relative bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
                  Real Stories, Real Results
                </span>
              </h2>
              
              {/* Animated customer count */}
              <div className="flex items-center justify-center gap-3 mb-2">
                <div className="flex -space-x-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary border-2 border-background flex items-center justify-center text-white text-xs font-bold">😊</div>
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-secondary to-accent border-2 border-background flex items-center justify-center text-white text-xs font-bold">👨</div>
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-accent to-primary border-2 border-background flex items-center justify-center text-white text-xs font-bold">👩</div>
                </div>
                <p className="text-xl text-muted-foreground">
                  Join <span className="font-bold text-primary">1,000+</span> Happy Clients
                </p>
              </div>
              
              {/* Decorative underline */}
              <div className="flex items-center justify-center gap-2">
                <Star className="w-4 h-4 text-accent fill-accent" />
                <div className="h-1 w-32 bg-gradient-to-r from-primary via-secondary to-accent rounded-full" />
                <Star className="w-4 h-4 text-accent fill-accent" />
              </div>
            </div>
            
            {/* Card Stack Effect */}
            <div className="relative">
              <div className="absolute inset-0 bg-card/50 rounded-2xl transform translate-x-2 translate-y-2 -z-10 blur-sm" />
              <div className="absolute inset-0 bg-card/30 rounded-2xl transform translate-x-4 translate-y-4 -z-20 blur-md" />
              
              <div className="relative">
                <TestimonialCarousel />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-secondary via-secondary to-primary relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="/images/cta-ready-to-start.jpg" 
            alt="Welcoming professional tax office entrance" 
            className="w-full h-full object-cover opacity-70"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-secondary/40 via-secondary/40 to-primary/40" />
        </div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.1),transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(255,255,255,0.05),transparent_50%)]"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center text-white space-y-6">
            <h2 className="text-3xl md:text-5xl font-bold animate-fade-in">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-white/90 animate-fade-in" style={{ animationDelay: "100ms" }}>
              Schedule your appointment today — it only takes a minute.
            </p>
            <div className="animate-fade-in" style={{ animationDelay: "200ms" }}>
              <Button asChild size="lg" variant="secondary" className="text-lg shadow-xl hover:shadow-2xl hover:scale-110 transition-all duration-300 animate-float">
                <Link to="/schedule-appointment">Schedule My Appointment</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Home;
